import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaArrowLeft,
  FaUsers,
  FaPercentage,
  FaYoutube,
  FaInstagram,
  FaFilter,
  FaPlay,
  FaPause,
  FaEye,
  FaChartLine,
  FaDollarSign,
  FaGlobe,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getBrandsByCompany } from '../../services/companyAuthService';
import { fetchCampaigns } from '../../../Redux/campaignSlice';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CampaignAnalytics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaigns, loading } = useSelector((state) => state.campaign);

  // State management
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchBrands();
    dispatch(fetchCampaigns());
  }, [dispatch]);

  useEffect(() => {
    if (selectedBrand && campaigns.length > 0) {
      const filtered = campaigns.filter(campaign => {
        const brandId = campaign.brandId?._id || campaign.brandId;
        return brandId === selectedBrand;
      });
      setFilteredCampaigns(filtered);
      // Reset campaign selection when brand changes
      setSelectedCampaign(null);
      setCampaignStats(null);
    }
  }, [selectedBrand, campaigns]);

  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandsByCompany();
      if (response.success) {
        setBrands(response.brands);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to fetch brands");
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchCampaignStats = useCallback(async () => {
    if (!selectedCampaign) return;

    try {
      setLoadingStats(true);

      // Initialize variables
      let influencerIds = new Set();
      let totalVisits = 0;
      let totalUniqueVisits = 0;
      let totalConversions = 0;

      // Fetch affiliate links for basic statistics
      try {
        const affiliateResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${selectedCampaign._id}/affiliate-links`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const linksData = await affiliateResponse.json();
        
        if (linksData.success && linksData.data) {
          const affiliateLinks = linksData.data || [];

          // Calculate basic statistics
          totalVisits = affiliateLinks.reduce((sum, link) => sum + (link.visits || 0), 0);
          totalUniqueVisits = affiliateLinks.reduce((sum, link) => sum + (link.uniqueVisits || 0), 0);
          totalConversions = affiliateLinks.reduce((sum, link) => sum + (link.conversions || 0), 0);

          // Collect influencer IDs from affiliate links
          affiliateLinks.forEach(link => {
            if (link.userId) {
              influencerIds.add(link.userId);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching affiliate links:', error);
      }

      // Fetch YouTube statistics
      let youtubeStats = {
        totalVideos: 0,
        approvedVideos: 0,
        totalViews: 0,
        totalLikes: 0,
        totalComments: 0,
        totalEngagements: 0
      };

      try {
        const youtubeResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${selectedCampaign._id}/youtube-stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const youtubeData = await youtubeResponse.json();
        
        if (youtubeData.success && youtubeData.data) {
          youtubeStats = youtubeData.data;
          
          // If we have YouTube data, it means at least one influencer has added media
          // We'll estimate the influencer count based on available data
          if (youtubeStats.totalVideos > 0) {
            // Since we can't get individual influencer IDs from stats endpoint,
            // we'll use the affiliate links count as a base and ensure it's at least 1 if media exists
            if (influencerIds.size === 0 && youtubeStats.totalVideos > 0) {
              influencerIds.add('estimated-influencer-1');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching YouTube stats:', error);
      }

      // Fetch Instagram statistics
      let instagramStats = {
        totalPosts: 0,
        approvedPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalSaves: 0,
        totalReachs: 0,
        totalEngagements: 0
      };

      try {
        const instagramResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${selectedCampaign._id}/instagram-stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const instagramData = await instagramResponse.json();
        
        if (instagramData.success && instagramData.data) {
          instagramStats = instagramData.data;
          
          // If we have Instagram data, it means at least one influencer has added media
          if (instagramStats.totalPosts > 0) {
            // Ensure we have at least one influencer if media exists
            if (influencerIds.size === 0 && instagramStats.totalPosts > 0) {
              influencerIds.add('estimated-influencer-1');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching Instagram stats:', error);
      }

      // Calculate conversion rate
      const conversionRate = totalVisits > 0 ? (totalConversions / totalVisits) * 100 : 0;

      // The influencer count should be at least 1 if any media exists
      const influencerCount = Math.max(influencerIds.size, 
        (youtubeStats.totalVideos > 0 || instagramStats.totalPosts > 0) ? 1 : 0
      );

      const finalStats = {
        affiliateLinks: [],
        influencerCount,
        totalVisits,
        totalUniqueVisits,
        totalConversions,
        conversionRate: conversionRate.toFixed(2),
        youtubeStats,
        instagramStats
      };

      setCampaignStats(finalStats);

    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      toast.error('Failed to fetch campaign statistics');
    } finally {
      setLoadingStats(false);
    }
  },[selectedCampaign])

  const fetchPaymentSummary = useCallback (async () => {
    if (!selectedCampaign) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${selectedCampaign._id}/payment-summary`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPaymentSummary(data.data);
      }
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      toast.error('Failed to fetch payment summary');
    }
  },[selectedCampaign]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignStats();
      fetchPaymentSummary();
    }
  }, [selectedCampaign, timeRange, fetchCampaignStats, fetchPaymentSummary]);
  

  const resetSelection = () => {
    setSelectedBrand('');
    setSelectedCampaign(null);
    setCampaignStats(null);
    setPaymentSummary(null);
  };

  const prepareChartData = () => {
    if (!campaignStats) {
      return null;
    }

    try {
      // Visits comparison chart
      const visitsData = {
        labels: ['Total Visits', 'Unique Visits'],
        datasets: [{
          label: 'Visits',
          data: [campaignStats.totalVisits, campaignStats.totalUniqueVisits],
          backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)'],
          borderColor: ['rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
          borderWidth: 2,
          borderRadius: 8,
        }],
      };

      // Conversion rate pie chart
      const conversionData = {
        labels: ['Conversions', 'Non-Conversions'],
        datasets: [{
          data: [campaignStats.totalConversions, campaignStats.totalVisits - campaignStats.totalConversions],
          backgroundColor: ['rgba(16, 185, 129, 0.8)', 'rgba(239, 68, 68, 0.8)'],
          borderColor: ['rgb(16, 185, 129)', 'rgb(239, 68, 68)'],
          borderWidth: 2,
        }],
      };

      // Social media performance chart
      const socialMediaData = {
        labels: ['YouTube Views', 'YouTube Engagements', 'Instagram Reachs', 'Instagram Engagements'],
        datasets: [{
          label: 'Performance Metrics',
          data: [
            campaignStats.youtubeStats.totalViews,
            campaignStats.youtubeStats.totalEngagements,
            campaignStats.instagramStats.totalReachs,
            campaignStats.instagramStats.totalEngagements
          ],
          backgroundColor: [
            'rgba(239, 68, 68, 0.8)',    // Red for YouTube
            'rgba(239, 68, 68, 0.6)',
            'rgba(236, 72, 153, 0.8)',   // Pink for Instagram
            'rgba(236, 72, 153, 0.6)'
          ],
          borderColor: ['rgb(239, 68, 68)', 'rgb(239, 68, 68)', 'rgb(236, 72, 153)', 'rgb(236, 72, 153)'],
          borderWidth: 2,
          borderRadius: 8,
        }],
      };

      return { visitsData, conversionData, socialMediaData };
    } catch (error) {
      console.error('Error preparing chart data:', error);
      return null;
    }
  };

  const renderBrandSelection = () => (
    <div className="w-full">
      <div className="mb-4">
        <label htmlFor="brand-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          Select Brand
        </label>
        <select
          id="brand-select"
          value={selectedBrand}
          onChange={(e) => setSelectedBrand(e.target.value)}
          className="w-full text-xs sm:text-base p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        >
          <option value="">-- Select a Brand --</option>
          {brands
            // .filter(brand => brand.isActive === true)
            .map((brand) => (
            <option key={brand._id} value={brand._id}>
              {brand.brandName}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderCampaignSelection = () => (
    <div className="w-full">
      <div className="mb-4">
        <label htmlFor="campaign-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          Select Campaign
        </label>
        <select
          id="campaign-select"
          value={selectedCampaign?._id || ''}
          onChange={(e) => {
            const campaign = filteredCampaigns.find(c => c._id === e.target.value);
            setSelectedCampaign(campaign);
          }}
          className="w-full text-xs sm:text-base p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          disabled={!selectedBrand}
        >
          <option value="">-- Select a Campaign --</option>
          {filteredCampaigns
            // .filter(campaign => campaign.status === 'active')
            .map((campaign) => (
              <option key={campaign._id} value={campaign._id}>
                {campaign.name} - {campaign.campaignType}
              </option>
            ))}
        </select>
      </div>
    </div>
  );

  const renderSelectionHeader = () => (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {/* Left Column - Brand Selection */}
        <div className="md:border-r md:border-gray-200 md:pr-6">
          {renderBrandSelection()}
        </div>

        {/* Right Column - Campaign Selection */}
        <div className="md:pl-6">
          {renderCampaignSelection()}
        </div>
      </div>

      {/* Selection Status */}
      {/* {selectedBrand && selectedCampaign && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg">
              <FaCheckCircle className="mr-2" />
              Ready to view analytics for: {selectedCampaign.name}
            </div>
          </div>
        </div>
      )} */}
    </div>
  );

  const renderAnalytics = () => {
    if (!selectedCampaign || !campaignStats) {
      return null;
    }

    const chartData = prepareChartData();

    return (
      <div className="max-w-7xl mx-auto">
        {/* Campaign Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-2 md:p-4 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="lg:text-2xl md:text-xl text-lg font-bold mb-2">{selectedCampaign.name}</h2>
              <p className="text-orange-100 mb-4 text-xs sm:text-sm">{selectedCampaign.description}</p>
              <div className="flex items-center space-x-2 sm:space-x-4 text-xs sm:text-sm flex-wrap">
                <span className="flex items-center">
                  <FaGlobe className="mr-2" />
                  {selectedCampaign.campaignType}
                </span>
                <span className="flex items-center">
                  <FaDollarSign className="mr-2" />
                  Commission: {selectedCampaign.commission?.amount || selectedCampaign.commission?.percentage || 'N/A'}
                  {selectedCampaign.commission?.percentage && '%'}
                </span>
                <span className={`px-1 md:px-2 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold ${selectedCampaign.status === 'active'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                  }`}>
                  {selectedCampaign.status === 'active' ? <FaPlay className="inline mr-1" /> : <FaPause className="inline mr-1" />}
                  {selectedCampaign.status}
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate(`/company/dashboard/campaigns/${selectedCampaign._id}/affiliate-stats`)}
              className="mt-4 md:mt-0 bg-white text-orange-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold hover:bg-orange-50 transition-colors flex items-center"
            >
              <FaUsers className="mr-2 text-xs sm:text-base" />
              View Influencers ({campaignStats.influencerCount})
            </button>
          </div>
        </div>

        {/* Time Range Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 flex items-center">
              <FaFilter className="mr-2 text-orange-500 text-xs sm:text-base" />
              Time Range Filter
            </h3>
            <div className="flex flex-wrap gap-2">
              {['7d', '30d', '90d', 'all'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${timeRange === range
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {range === 'all' ? 'All Time' : range}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        {paymentSummary && paymentSummary.summary && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-8">
            <div className="flex items-center mb-6">
              <FaDollarSign className="text-green-500 text-xl sm:text-2xl mr-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Payment Summary</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Total Due Amount */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border-l-4 border-red-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-red-600 mb-1 font-medium">Total Due Amount</p>
                    <p className="text-sm md:text-base lg:text-lg font-bold text-red-800">
                      {paymentSummary.currency?.symbol || paymentSummary.currency || '₹'} {paymentSummary.summary.totalDue.toLocaleString()}
                    </p>
                    <p className="text-xs sm:text-sm text-red-500 mt-1">
                      Amount owed to all affiliates
                    </p>
                  </div>
                  {/* <div className="bg-red-500 text-white rounded-full p-3">
                    <FaDollarSign className="text-xl" />
                  </div> */}
                </div>
              </div>

              {/* Total Paid Amount */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-green-600 mb-1 font-medium">Total Paid Amount</p>
                    <p className="text-sm md:text-base lg:text-lg font-bold text-green-800">
                      {paymentSummary.currency?.symbol || paymentSummary.currency || '₹'} {paymentSummary.summary.totalPaid.toLocaleString()}
                    </p>
                    <p className="text-xs sm:text-sm text-green-500 mt-1">
                      Amount already paid to affiliates
                    </p>
                  </div>
                  {/* <div className="bg-green-500 text-white rounded-full p-3">
                    <FaChartLine className="text-xl" />
                  </div> */}
                </div>
              </div>

              {/* Remaining Due Amount */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-l-4 border-orange-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-orange-600 mb-1 font-medium">Total Unpaid Amount</p>
                    <p className="text-sm md:text-base lg:text-lg font-bold text-orange-800">
                      {paymentSummary.currency?.symbol || paymentSummary.currency || '₹'} {paymentSummary.summary.totalUnpaid.toLocaleString()}
                    </p>
                    <p className="text-xs sm:text-sm text-orange-500 mt-1">
                      Amount still unpaid
                    </p>
                  </div>
                  {/* <div className="bg-orange-500 text-white rounded-full p-3">
                    <FaPercentage className="text-xl" />
                  </div> */}
                </div>
              </div>

              {/* Payment Completion Rate */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-blue-600 mb-1 font-medium">Payment Progress</p>
                    <p className="text-sm md:text-base lg:text-lg font-bold text-blue-800">
                      {paymentSummary.summary.paymentCompletionRate}%
                    </p>
                    <p className="text-xs sm:text-sm text-blue-500 mt-1">
                      Completion rate
                    </p>
                  </div>
                  {/* <div className="bg-blue-500 text-white rounded-full p-3">
                    <FaUsers className="text-xl" />
                  </div> */}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Payment Progress</span>
                <span className="text-xs sm:text-sm text-gray-500">
                  {paymentSummary.summary.totalPaid.toLocaleString()} / {paymentSummary.summary.totalDue.toLocaleString()} {paymentSummary.currency?.symbol || paymentSummary.currency || '₹'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 h-2 sm:h-3 rounded-full transition-all duration-500"
                  style={{ width: `${paymentSummary.summary.paymentCompletionRate}%` }}
                ></div>
              </div>
            </div>

            {/* Affiliate Count */}
            <div className="mt-4 text-center">
              <span className="inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-700 rounded-lg text-xs sm:text-sm font-medium">
                <FaUsers className="mr-2" />
                {paymentSummary.summary.totalAffiliates} Affiliates in this campaign
              </span>
            </div>
          </div>
        )}

        {/* Affiliate Performance Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-8">
          <div className="flex items-center mb-6">
            <FaChartLine className="text-orange-500 text-xl sm:text-2xl mr-3" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Affiliate Performance</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-blue-600 mb-1 font-medium">Total Visits</p>
                  <p className="text-sm md:text-base lg:text-lg font-bold text-blue-800">{campaignStats.totalVisits.toLocaleString()}</p>
                </div>
                <div className="bg-blue-500 text-white rounded-full p-3">
                  <FaEye className="text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-orange-600 mb-1 font-medium">Unique Visits</p>
                  <p className="text-sm md:text-base lg:text-lg font-bold text-orange-800">{campaignStats.totalUniqueVisits.toLocaleString()}</p>
                </div>
                <div className="bg-orange-500 text-white rounded-full p-3">
                  <FaUsers className="text-xl" />
                </div>
              </div>
            </div>  

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-green-600 mb-1 font-medium">Conversions</p>
                  <p className="text-sm md:text-base lg:text-lg font-bold text-green-800">{campaignStats.totalConversions.toLocaleString()}</p>
                </div>
                <div className="bg-green-500 text-white rounded-full p-3">
                  <FaChartLine className="text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-purple-600 mb-1 font-medium">Conversion Rate</p>
                  <p className="text-sm md:text-base lg:text-lg font-bold text-purple-800">{campaignStats.conversionRate}%</p>
                </div>
                <div className="bg-purple-500 text-white rounded-full p-3">
                  <FaPercentage className="text-xl" />
                </div>
              </div>
            </div>

            {/* <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 mb-1 font-medium">Total Influencers</p>
                  <p className="text-3xl font-bold text-orange-800">{campaignStats.influencerCount}</p>
                </div>
                <div className="bg-orange-500 text-white rounded-full p-3">
                  <FaUsers className="text-xl" />
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Social Media Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* YouTube Stats */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex items-center mb-6">
              <FaYoutube className="text-red-500 text-xl sm:text-2xl mr-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">YouTube Performance</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.youtubeStats.totalVideos}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Videos</p>
                <p className="text-xs sm:text-sm text-green-600">({campaignStats.youtubeStats.approvedVideos} approved)</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.youtubeStats.totalViews.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Views</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.youtubeStats.totalLikes.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Likes</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.youtubeStats.totalEngagements.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Engagements</p>
              </div>
            </div>
          </div>

          {/* Instagram Stats */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex items-center mb-6">
              <FaInstagram className="text-pink-500 text-xl sm:text-2xl mr-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Instagram Performance</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.instagramStats.totalPosts}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Posts</p>
                <p className="text-xs sm:text-sm text-green-600">({campaignStats.instagramStats.approvedPosts} approved)</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                  <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.instagramStats.totalReachs.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Reachs</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.instagramStats.totalLikes.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Likes</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{campaignStats.instagramStats.totalEngagements.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-600">Engagements</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {chartData && chartData.visitsData && chartData.conversionData && chartData.socialMediaData ? (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-6">Performance Analytics</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
              <div>
                <h4 className="text-xs sm:text-lg font-semibold mb-4 text-gray-700">Visits Comparison</h4>
                <Bar
                  data={chartData.visitsData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                      }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </div>

              <div>
                <h4 className="text-xs sm:text-lg font-semibold mb-4 text-gray-700">Conversion Distribution</h4>
                <Pie
                  data={chartData.conversionData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-8">
              <h4 className="text-xs sm:text-lg font-semibold mb-4 text-gray-700">Social Media Performance Overview</h4>
              <Bar
                data={chartData.socialMediaData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: 'white',
                      bodyColor: 'white',
                    }
                  },
                  scales: {
                    y: { beginAtZero: true }
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-6">Performance Analytics</h3>
            <div className="text-center text-gray-500 py-8">
              <p className="text-xs sm:text-sm">Chart data is not available at the moment.</p>
              <p className="text-xs sm:text-sm mt-2">Debug: chartData = {chartData ? 'Available' : 'Not available'}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading || loadingBrands) {
    return (
      <div className="h-[calc(100dvh-80px)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-xs sm:text-sm md:text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 py-6 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="flex items-center justify-between p-2 sm:p-4 mb-2 sm:mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-orange-500 hover:text-orange-600 transition-colors text-sm sm:text-base"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>

          {(selectedBrand || selectedCampaign) && (
            <button
              onClick={resetSelection}
              className="px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Reset Selection
            </button>
          )}
        </div>

        {/* Page Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Campaign Analytics</h2>
          <p className="text-gray-600 text-xs sm:text-sm">Analyze your campaign performance and insights</p>
        </div>

        {/* Two-Column Selection Header */}
        {renderSelectionHeader()}

        {/* Analytics Content */}
        {selectedCampaign && campaignStats && renderAnalytics()}

        {/* Loading overlay for stats */}
        {loadingStats && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600 text-xs sm:text-sm">Loading campaign statistics...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAnalytics; 